import { useMemo } from "react";
import type { TalkJSData } from "../chat.types";
import type { Circle } from "@core/types/core.types";
import { isChatEnabled } from "../utils";

export type UseChatStatusArgs = {
  /** TalkJS data from the server */
  talkjsData: TalkJSData | null;
  /** User's circles */
  userCircles: Circle[] | null | undefined;
};

export type UseChatStatusResult = {
  /** Whether chat is enabled for this user */
  isChatEnabled: boolean;
  /** Whether we're still loading TalkJS data */
  isLoading: boolean;
  /** The TalkJS data (for additional info) */
  chatStatus: TalkJSData | null;
};

/**
 * Determines chat status based on TalkJS config and user circles.
 *
 * @example
 * ```typescript
 * const { isChatEnabled, isLoading } = useChatStatus({
 *   talkjsData,
 *   userCircles: user?.circles,
 * });
 * ```
 */
export function useChatStatus({
  talkjsData,
  userCircles,
}: UseChatStatusArgs): UseChatStatusResult {
  const enabled = useMemo(
    () => isChatEnabled({ talkjsData, userCircles }),
    [talkjsData, userCircles]
  );

  return {
    isChatEnabled: enabled,
    isLoading: !talkjsData,
    chatStatus: talkjsData,
  };
}

export default useChatStatus;

